import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const VISIBILITY_MODULES = [
  {
    key: 'client_can_view_overview',
    label: 'Overview',
    icon: 'LayoutDashboard',
    description: 'Site overview and summary dashboard',
  },
  {
    key: 'client_can_view_notes',
    label: 'Notes',
    icon: 'StickyNote',
    description: 'Shared notes and communications',
  },
  {
    key: 'client_can_view_photos',
    label: 'Photos',
    icon: 'Images',
    description: 'Photo gallery and progress images',
  },
  {
    key: 'client_can_view_documents',
    label: 'Documents',
    icon: 'FileText',
    description: 'Shared documents and files',
  },
  {
    key: 'client_can_view_drawings',
    label: 'Drawings',
    icon: 'FileImage',
    description: 'Drawings and plans',
  },
  {
    key: 'client_can_view_snags',
    label: 'Snags',
    icon: 'ClipboardList',
    description: 'Snag list and defect tracking',
  },
  {
    key: 'client_can_view_reports',
    label: 'Reports',
    icon: 'BarChart2',
    description: 'Site reports and summaries',
  },
  {
    key: 'client_can_view_activity_timeline',
    label: 'Activity Timeline',
    icon: 'Activity',
    description: 'Activity feed and site events',
  },
  {
    key: 'client_can_view_site_diary',
    label: 'Site Diary',
    icon: 'CalendarCheck',
    description: 'Daily site diary and progress updates',
  },
  {
    key: 'client_can_view_rams',
    label: 'H&S Docs',
    icon: 'ShieldCheck',
    description: 'Policies, risk assessments, method statements and COSHH',
  },
  {
    key: 'client_can_view_toolbox_talks',
    label: 'Toolbox Talks',
    icon: 'MessageSquare',
    description: 'Safety briefings and toolbox talk records',
  },
  {
    key: 'client_can_view_inductions',
    label: 'Inductions',
    icon: 'UserCheck',
    description: 'Site induction records and completions',
  },
  {
    key: 'client_can_view_compliance',
    label: 'Compliance / Certifications',
    icon: 'BadgeCheck',
    description: 'Compliance summary and certification status',
  },
];

const DEFAULT_VISIBILITY = VISIBILITY_MODULES?.reduce((acc, m) => {
  acc[m.key] = true;
  return acc;
}, {});

const ClientVisibilitySection = ({ siteId }) => {
  const [visibility, setVisibility] = useState({ ...DEFAULT_VISIBILITY });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);

  const loadVisibility = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        ?.from('sites')
        ?.select(VISIBILITY_MODULES?.map(m => m?.key)?.join(', '))
        ?.eq('id', siteId)
        ?.single();

      if (err) throw err;

      if (data) {
        const loaded = {};
        VISIBILITY_MODULES?.forEach(m => {
          loaded[m.key] = data?.[m?.key] ?? true;
        });
        setVisibility(loaded);
      }
    } catch (err) {
      console.error('[ClientVisibilitySection] load settings failed:', err);
      setError('Failed to load visibility settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadVisibility();
  }, [loadVisibility]);

  const handleToggle = (key) => {
    setVisibility(prev => ({ ...prev, [key]: !prev?.[key] }));
    setDirty(true);
    setSuccess('');
  };

  const handleEnableAll = () => {
    const all = {};
    VISIBILITY_MODULES?.forEach(m => { all[m.key] = true; });
    setVisibility(all);
    setDirty(true);
    setSuccess('');
  };

  const handleDisableAll = () => {
    const none = {};
    VISIBILITY_MODULES?.forEach(m => { none[m.key] = false; });
    setVisibility(none);
    setDirty(true);
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // `.select().maybeSingle()` forces PostgREST to return the updated row
      // so we can detect silent no-op writes (RLS filtering the row out, or
      // an id mismatch). Without this, Supabase returns { error: null } with
      // zero rows affected and the UI shows a "successful" save that never
      // persisted. Mirrors the pattern from taskPhotoService (commit 10a655a).
      const { data, error: err } = await supabase
        ?.from('sites')
        ?.update(visibility)
        ?.eq('id', siteId)
        ?.select('id')
        ?.maybeSingle();

      if (err) throw err;
      if (!data) throw new Error('Visibility settings did not save — permission denied or site not found.');
      setSuccess('Client visibility settings saved successfully.');
      setDirty(false);
    } catch (err) {
      console.error('[ClientVisibilitySection] save settings failed:', err);
      setError(err?.message || 'Failed to save visibility settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadVisibility();
    setDirty(false);
    setSuccess('');
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Icon name="Loader2" size={24} className="animate-spin text-accent" />
        <span className="ml-2 text-sm text-muted-foreground">Loading settings…</span>
      </div>
    );
  }

  const enabledCount = Object.values(visibility)?.filter(Boolean)?.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Icon name="Eye" size={18} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Client Access / Client Visibility</h3>
            <p className="text-xs text-muted-foreground">
              Control exactly what the assigned client can see in the portal for this site.{' '}
              <span className="font-medium text-foreground">{enabledCount} of {VISIBILITY_MODULES?.length}</span> sections enabled.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleEnableAll}
            className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
          >
            Enable All
          </button>
          <button
            type="button"
            onClick={handleDisableAll}
            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
          >
            Disable All
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-3.5 bg-blue-50 border border-blue-200 rounded-xl">
        <Icon name="Info" size={15} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 leading-relaxed">
          These settings apply to the <strong>assigned client</strong> for this site. Sections turned off will be completely hidden from the client portal — they will not appear in the menu or be accessible via direct URL.
        </p>
      </div>

      {/* Feedback */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <Icon name="AlertCircle" size={15} className="flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
          <Icon name="CheckCircle2" size={15} className="flex-shrink-0" />
          Visibility settings saved.
        </div>
      )}

      {/* Module toggles */}
      <div className="space-y-2">
        {VISIBILITY_MODULES?.map((module) => {
          const isOn = visibility?.[module.key] ?? true;
          return (
            <div
              key={module.key}
              className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-all ${
                isOn
                  ? 'bg-card border-border' :'bg-muted/30 border-border/60 opacity-75'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isOn ? 'bg-accent/10' : 'bg-muted'
                }`}>
                  <Icon
                    name={module.icon}
                    size={16}
                    className={isOn ? 'text-accent' : 'text-muted-foreground'}
                  />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${isOn ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {module.label}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{module.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  isOn
                    ? 'bg-emerald-100 text-emerald-700' :'bg-muted text-muted-foreground'
                }`}>
                  {isOn ? 'Visible' : 'Hidden'}
                </span>
                <button
                  type="button"
                  onClick={() => handleToggle(module.key)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent/30 ${
                    isOn ? 'bg-accent' : 'bg-muted-foreground/30'
                  }`}
                  aria-label={`Toggle ${module.label} visibility`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                      isOn ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save / Discard */}
      <div className="flex items-center gap-3 pt-2 border-t border-border">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="flex items-center gap-2 px-5 py-2.5 bg-accent text-accent-foreground rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <><Icon name="Loader2" size={15} className="animate-spin" /> Saving...</>
          ) : (
            <><Icon name="Save" size={15} /> Save Changes</>
          )}
        </button>
        {dirty && (
          <button
            onClick={handleReset}
            className="px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Discard
          </button>
        )}
        {!dirty && (
          <p className="text-xs text-muted-foreground">All changes saved.</p>
        )}
      </div>
    </div>
  );
};

export default ClientVisibilitySection;
